import reducerRegistry from '@comeon/reducer-registry';
import { requestApi, parseResponse } from '~/services/api.js';
import { createImageViewFactory } from '~/components/utilities/image-format';
import getGameUrls from '~/components/casino-shared/utilities/getGameUrls';
import enhanceImageView from '~/components/casino-shared/utilities/enhanceImage';

const REDUCER_NAME = 'subNavigation';
const host = '/';

export const FETCH_CASINO_SUBMENU_GAMES_START =
	'@komigen/mobile-frontend/submenu-games/FETCH_CASINO_SUBMENU_GAMES_START';

export const FETCH_CASINO_SUBMENU_GAMES_SUCCESS =
	'@komigen/mobile-frontend/submenu-games/FETCH_CASINO_SUBMENU_GAMES_SUCCESS';

export const FETCH_CASINO_SUBMENU_GAMES_FAILURE =
	'@komigen/mobile-frontend/submenu-games/FETCH_CASINO_SUBMENU_GAMES_FAILURE';

const fetchSubmenuGames = isLive => {
	return async (dispatch, getState, { isPictureElementEnabled, imageDescriptors, imageFormats }) => {
		const state = getState();
		const { casino, liveCasino } = state[REDUCER_NAME];

		if (isLive ? liveCasino.hydrated || liveCasino.loading : casino.hydrated || casino.loading) {
			return;
		}

		dispatch({
			type: FETCH_CASINO_SUBMENU_GAMES_START,
			payload: {
				isLive
			}
		});

		const resourcePath = `rest/state/casino/submenu?product=${isLive ? 'LIVE_CASINO' : 'CASINO'}`;

		try {
			const response = parseResponse(
				await requestApi(`${host}${resourcePath}`, {}, 'GET', false, false, {}, { cache: 'default' })
			);

			dispatch({
				type: FETCH_CASINO_SUBMENU_GAMES_SUCCESS,
				payload: {
					submenus: response,
					isPictureElementEnabled,
					imageDescriptors,
					imageFormats,
					isLive
				}
			});
		} catch (e) {
			dispatch({
				type: FETCH_CASINO_SUBMENU_GAMES_FAILURE,
				payload: {
					isLive
				}
			});
		}
	};
};

const initialState = {
	casino: {
		hydrated: false,
		loading: false,
		games: {},
		categoriesBySubmenu: {}
	},
	liveCasino: {
		hydrated: false,
		loading: false,
		games: {},
		categoriesBySubmenu: {}
	}
};

// eslint-disable-next-line default-param-last
const reducer = (state = initialState, { type, payload }) => {
	switch (type) {
		case FETCH_CASINO_SUBMENU_GAMES_START:
			return {
				...state,
				[payload.isLive ? 'liveCasino' : 'casino']: {
					...state[payload.isLive ? 'liveCasino' : 'casino'],
					loading: true,
					hydrated: false
				}
			};
		case FETCH_CASINO_SUBMENU_GAMES_SUCCESS: {
			const { isPictureElementEnabled, imageDescriptors, imageFormats, submenus = [], isLive } = payload;

			const formatEnhancer = isPictureElementEnabled && createImageViewFactory(imageFormats, imageDescriptors);

			const categoriesBySubmenu = submenus.reduce((acc, submenu) => {
				return {
					...acc,
					[submenu.urlPath]: submenu.submenuCategories.reduce((prev, curr) => {
						const { games, ...rest } = curr;

						return [...prev, { ...rest, gameCodes: games.map(game => game.code) }];
					}, [])
				};
			}, {});

			const flattenedGames = submenus.flatMap(submenu =>
				submenu.submenuCategories.flatMap(category => {
					return category.games.map(game => {
						const enhancedGame = { ...game, ...getGameUrls(game) };

						if (enhancedGame.imageView) {
							enhancedGame.imageView = enhanceImageView(
								enhancedGame.imageView,
								imageDescriptors,
								formatEnhancer
							);
						}

						return [game.code, enhancedGame];
					});
				})
			);

			let games = Object.fromEntries(flattenedGames);
			// adding jackpot info on the games
			const jackpotSubmenu = submenus.find(submenu => submenu.type === 'JACKPOT');

			if (jackpotSubmenu) {
				// TODO: remove this manual data traversal when backend adds jackpot identifier on the game object
				// as of now backend does not return the jackpot id or name on the game objects,
				// so we find the relation on client side and match them manually
				games = jackpotSubmenu.submenuCategories
					.filter(category => category.type === 'JACKPOT')
					.reduce((prevGames, category) => {
						const categoryGameCodes = category.games.map(game => game.code);
						const jackpotEnhancedGames = categoryGameCodes.reduce((acc, code) => {
							acc[code] = { ...games[code], jackpotName: category.jackpotName };

							return acc;
						}, {});

						return { ...prevGames, ...jackpotEnhancedGames };
					}, games);
			}

			return {
				...state,
				[isLive ? 'liveCasino' : 'casino']: {
					games,
					categoriesBySubmenu,
					hydrated: true,
					loading: false
				}
			};
		}
		case FETCH_CASINO_SUBMENU_GAMES_FAILURE: {
			return {
				...state,
				[payload.isLive ? 'liveCasino' : 'casino']: {
					...state[payload.isLive ? 'liveCasino' : 'casino'],
					loading: false,
					hydrated: false
				}
			};
		}
		default:
			return state;
	}
};

reducerRegistry.register(REDUCER_NAME, reducer);

export { fetchSubmenuGames };

export default reducer;
